import { FlowStage } from "@finq/app-base/types/flowStage.namespace"

export const usePensionFlowStageManager = defineStore("pensionFlowStages", () => {
  const stageManager = useFlowStageManager("PENSIONS")

  // Fetch the stage data as soon as the store is created
  stageManager.refetchStage()

  return stageManager
})

export const useFlowManagerByFlowKey = (flowKey: string) => {
  switch (flowKey) {
    case FlowStage.FlowKey.PENSIONS:
      return usePensionFlowStageManager()

    default:
      return {
        currentStage: ref<Nullable<FlowStage.StageKey>>(null),
        isStageLoading: ref(false),
        refetchStage: () => {},
        updateStageMutation: () => {},
      } as unknown as ReturnType<typeof usePensionFlowStageManager>
  }
}

export const useFlowStageManager = (flowKey?: FlowStage.FlowKey) => {
  const currentStage = ref<Nullable<FlowStage.StageKey>>(null)
  const user = useUser()

  const fetchStageQuery = useFetchFlowStage(
    { flowKey: ref(flowKey!) },
    {
      throwOnError: true,
      enabled: computed(() => {
        return Boolean(flowKey) && user.isAuthenticated.value && import.meta.client
      }),
    }
  )
  const { data: stageData, isLoading, refetch } = fetchStageQuery

  const updateStageMutation = useUpdateFlowStage(
    { flowKey: ref(flowKey!) },
    {
      onSuccess(data) {
        currentStage.value = data.currentStage as FlowStage.StageKey
      },
    }
  )

  async function refetchStage() {
    if (!flowKey || !user.isAuthenticated.value || !import.meta.client) return
    const data = await refetch()
    currentStage.value = data.data?.currentStage as FlowStage.StageKey
  }

  watch(
    () => stageData.value?.currentStage,
    (newStage) => {
      currentStage.value = newStage as FlowStage.StageKey
    }
  )

  return {
    currentStage,
    isStageLoading: isLoading,
    fetchStageQuery,
    updateStageMutation,
    refetchStage,
  }
}
